import { useState, useCallback, createContext, useContext } from "react";
import { AlertTriangle, X } from "lucide-react";

const ConfirmContext = createContext({ confirm: async () => true });

export function ConfirmProvider({ children }) {
    const [state, setState] = useState(null);
    const confirm = useCallback((opts = {}) => {
        return new Promise((resolve) => {
            setState({
                title: opts.title || "Are you sure?",
                message: opts.message || "This action can't be undone.",
                confirmLabel: opts.confirmLabel || "Delete",
                cancelLabel: opts.cancelLabel || "Cancel",
                danger: opts.danger !== false,
                resolve,
            });
        });
    }, []);
    const close = (answer) => { state?.resolve(answer); setState(null); };
    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}
            {state && (
                <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" onClick={() => close(false)} data-testid="confirm-dialog">
                    <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between p-5 border-b border-[var(--border)]">
                            <div className="flex items-start gap-3">
                                {state.danger && <AlertTriangle size={18} className="text-[var(--danger)] shrink-0 mt-0.5" />}
                                <div>
                                    <p className="label-tiny mb-1">/ Heads up</p>
                                    <h3 className="font-serif text-2xl">{state.title}</h3>
                                </div>
                            </div>
                            <button onClick={() => close(false)} aria-label="Close"><X size={16} /></button>
                        </div>
                        <div className="p-5 text-sm text-[var(--text-muted)]">{state.message}</div>
                        <div className="border-t border-[var(--border)] p-4 flex gap-2 justify-end">
                            <button onClick={() => close(false)} className="btn-ghost" data-testid="confirm-cancel">{state.cancelLabel}</button>
                            <button
                                onClick={() => close(true)}
                                className={`px-4 py-2 font-mono text-xs tracking-[0.2em] uppercase border ${state.danger ? "border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white" : "border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[#0a0908]"}`}
                                data-testid="confirm-ok"
                            >
                                {state.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    return useContext(ConfirmContext).confirm;
}
